import { Link } from "react-router-dom";
import type { PurchasesIvaSummary } from "../../api/types";
import { calendarDayText, money } from "../../i18n/intl";
import { notCountedReason } from "../../utils/paperList";

type ToFix = PurchasesIvaSummary["notCounted"]["facturasToFix"];

/**
 * G-164 (KI-110; owner 2026-10-16: "we need to warn about what is missing").
 * Every read factura the card cannot count, any month, with why and a link to
 * its page where the missing data is typed; and the typed IVA waiting on such
 * a factura (option C: not added until the factura counts). Labels, numbers
 * and pills only. Nothing is added up
 * here — the server sends the counts and the sums.
 */
export function PurchasesToFix({
  facturas,
  waitingOnUncounted,
}: {
  facturas: ToFix;
  waitingOnUncounted: PurchasesIvaSummary["typed"]["underUncountedFactura"];
}) {
  if (facturas.length === 0 && waitingOnUncounted.count === 0) return null;
  const byReason = new Map<string, number>();
  for (const f of facturas) {
    const reason = notCountedReason(f.reason) ?? f.reason;
    byReason.set(reason, (byReason.get(reason) ?? 0) + 1);
  }
  return (
    <div className="purch-to-fix" role="status">
      {facturas.length > 0 ? (
        <>
          <div className="purch-to-fix-head">
            <strong>Not counted: {facturas.length}</strong>
            {[...byReason].map(([reason, n]) => (
              <span key={reason} className="paper-pill paper-pill--warn">
                {reason}: {n}
              </span>
            ))}
          </div>
          <ul className="purch-not-counted-papers">
            {facturas.map((f) => (
              <li key={f.id}>
                <Link to={`/app/invoices/${f.id}`}>
                  {f.supplierName} {f.invoiceNumber ?? "(no number)"}
                </Link>{" "}
                <span className="paper-pill paper-pill--warn">{notCountedReason(f.reason)}</span>
                <span className="muted">
                  {" "}
                  · {f.invoiceDate ? calendarDayText(f.invoiceDate) : "no date"} · {f.locationName}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {waitingOnUncounted.count > 0 ? (
        <div className="purch-to-fix-head">
          <strong>Typed IVA waiting on a factura not counted</strong>
          <span className="paper-pill paper-pill--warn">
            {money(waitingOnUncounted.ivaEur)} IVA ·{" "}
            {waitingOnUncounted.count} {waitingOnUncounted.count === 1 ? "purchase" : "purchases"}
          </span>
        </div>
      ) : null}
    </div>
  );
}
